import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkLoginStatus, selectUserInfo, getApiBaseUrl, updateUserProfile, getUserById } from "../redux/WithcookieSlice";
import { useTranslation } from "../redux/TranslationHelper";

const UserProfileComponent = ({ 
  style,
  content = {},
  data = {},
  componentProps = {},
  pathVariables = {},
  varFields = {
    var01: { 
      show: false, 
      label: "", 
    },
    var02: { 
      show: false, 
      label: "", 

    },
    var03: { 
      show: false, 
      label: "", 

    }
  }
}) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [referralLink, setReferralLink] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedUserInfo, setEditedUserInfo] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isReloading, setIsReloading] = useState(false);
  const [highlightedFields, setHighlightedFields] = useState({});
  
  // withcookieData에서 테마 색상 가져오기
  const withcookieData = useSelector(state => state.withcookie.withcookieData);
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";
  const successColor = colorSet?.tertiary || "#28a745";

  // 스타일 설정
  const defaultStyle = {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Noto Sans KR, sans-serif',
    maxWidth: '800px',
    margin: '20px auto',
    position: 'relative',
    ...style
  };

  // 사용자 정보 조회
  useEffect(() => {
    const loadUserInfo = async () => {
      // 먼저 기본 로그인 상태 확인하여 사용자 ID 얻기
      const loginAction = await dispatch(checkLoginStatus({ forceRefresh: true }));
      
      // 로그인 상태이고 사용자 정보가 있으면 getUserById로 상세 정보 조회
      if (checkLoginStatus.fulfilled.match(loginAction) && loginAction.payload.user) {
        const userData = loginAction.payload.user;
        const userId = userData.id;
        const companyId = userData.company_id || 21;
        
        // getUserById 액션으로 사용자 상세 정보 조회
        await dispatch(getUserById({ userId, companyId }));
      }
    };
    
    loadUserInfo();
  }, [dispatch]);

  // 추천링크 생성
  useEffect(() => {
    if (userInfo && userInfo.insungId) {
      const host = window.location.host;
      setReferralLink(`https://${host}/signup/${userInfo.insungId}`);
    }
  }, [userInfo]);

  // 수정 모드로 전환될 때 현재 사용자 정보로 편집 상태 초기화
  useEffect(() => {
    if (userInfo && isEditing) {
      setEditedUserInfo({
        name: userInfo.name || '',
        phone: userInfo.phone || '',
        email: userInfo.email || '',
        birthday: userInfo.birthdate || userInfo.birthday || '',
        address: userInfo.address || '',
        var01: userInfo.var01 || '',
        var02: userInfo.var02 || '',
        var03: userInfo.var03 || '',
      });
    }
  }, [isEditing, userInfo]);
  
  // 하이라이트 효과 초기화
  useEffect(() => {
    if (Object.keys(highlightedFields).length > 0) {
      const timer = setTimeout(() => {
        setHighlightedFields({});
      }, 3000); // 3초 후 하이라이트 효과 제거
      
      return () => clearTimeout(timer);
    }
  }, [highlightedFields]);

  // 클립보드에 복사 기능
  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000); // 3초 후 메시지 숨김
      })
      .catch(err => {
        console.error(t('클립보드 복사 실패:'), err);
      });
  };

  // 수정 시작
  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };

  // 수정 취소
  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  // 입력 필드 변경 처리
  const handleInputChange = (field, value) => {
    setEditedUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 사용자 정보 다시 로드
  const reloadUserInfo = async () => {
    setIsReloading(true);
    try {
      // 사용자 ID와 회사 ID 확인
      if (!userInfo || !userInfo.id) {
        throw new Error(t('사용자 정보가 없습니다. 로그인이 필요합니다.'));
      }
      
      const userId = userInfo.id;
      const companyId = userInfo.company_id || 21; // 기본값으로 21 사용
      
      // getUserById 액션 디스패치
      const resultAction = await dispatch(getUserById({ userId, companyId }));
      
      // 성공/실패 처리
      if (getUserById.fulfilled.match(resultAction)) {
      } else {
        console.error(t("사용자 정보 새로고침 실패:"), resultAction.error);
        // 실패 시 기존 방식으로 다시 시도
        await dispatch(checkLoginStatus({ forceRefresh: true }));
      }
    } catch (error) {
      console.error(t("사용자 정보 로드 오류:"), error);
      // 오류 발생 시 기존 방식으로 다시 시도
      try {
        await dispatch(checkLoginStatus({ forceRefresh: true }));
      } catch (retryError) {
        console.error(t("기존 방식으로 재시도 실패:"), retryError);
      }
    } finally {
      setIsReloading(false);
    }
  };

  // 수정 내용 저장
  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // 변경된 필드 확인
      const changedFields = {};
      if (userInfo) {
        if (editedUserInfo.name !== userInfo.name) changedFields.name = true;
        if (editedUserInfo.phone !== userInfo.phone) changedFields.phone = true;
        if (editedUserInfo.email !== userInfo.email) changedFields.email = true;
        if (editedUserInfo.birthday !== (userInfo.birthdate || userInfo.birthday)) changedFields.birthday = true;
        if (editedUserInfo.address !== userInfo.address) changedFields.address = true;
        if (editedUserInfo.var01 !== userInfo.var01) changedFields.var01 = true;
        if (editedUserInfo.var02 !== userInfo.var02) changedFields.var02 = true;
        if (editedUserInfo.var03 !== userInfo.var03) changedFields.var03 = true;
        if (editedUserInfo.password && editedUserInfo.password.trim() !== '') changedFields.password = true;
      }
      
      // updateUserProfile 액션을 통해 사용자 정보 업데이트
      const resultAction = await dispatch(updateUserProfile(editedUserInfo));
      
      if (updateUserProfile.fulfilled.match(resultAction)) {
        // 성공적으로 업데이트된 경우
        setIsEditing(false);
        
        // GET 요청을 보내 최신 사용자 정보 가져오기
        await reloadUserInfo();
        
        // 변경된 필드 하이라이트 설정
        setHighlightedFields(changedFields);
        
        alert(t('회원 정보가 성공적으로 수정되었습니다.'));
      } else {
        // 오류 처리
        if (resultAction.payload) {
          setSaveError(resultAction.payload.message || t('회원 정보 수정 중 오류가 발생했습니다.'));
        } else {
          setSaveError(t('회원 정보 수정 중 오류가 발생했습니다.'));
        }
      }
    } catch (error) {
      console.error(t('프로필 업데이트 오류:'), error);
      setSaveError(t('회원 정보 수정 중 오류가 발생했습니다.'));
    } finally {
      setIsSaving(false);
    }
  };

  // 생년월일 포맷팅 함수
  const formatBirthdate = (date) => {
    if (!date) return '-';
    
    // 날짜 문자열인 경우 처리
    if (typeof date === 'string') {
      // ISO 날짜 형식(YYYY-MM-DDTHH:mm:ss.sssZ)인 경우 날짜 부분만 추출
      if (date.includes('T')) {
        return date.split('T')[0];
      }
      
      // 이미 YYYY-MM-DD 형식이면 그대로 반환
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // 다른 형식의 날짜 문자열인 경우 Date 객체로 변환 후 처리
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return date; // 유효하지 않은 날짜면 원본 반환
        return dateObj.toISOString().split('T')[0];
      } catch (e) {
        return date; // 변환 실패시 원본 반환
      }
    }
    
    // Date 객체인 경우 처리
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    
    return date; // 다른 타입이면 그대로 반환
  };

  // 프로필 정보 행 스타일
  const profileRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '15px',
    width: '100%',
    borderBottom: '1px solid #eee',
    paddingBottom: '15px'
  };

  // 프로필 라벨 스타일
  const labelStyle = {
    flex: '0 0 120px',
    fontWeight: 'bold',
    color: '#555'
  };

  // 프로필 값 스타일
  const valueStyle = {
    flex: '1 1 calc(100% - 135px)'
  };

  // 하이라이트된 값 스타일
  const getHighlightedStyle = (field) => {
    return highlightedFields[field] ? {
      backgroundColor: '#e6f7ff',
      padding: '5px',
      borderRadius: '4px',
      transition: 'background-color 0.5s ease',
      animation: 'fadeHighlight 3s'
    } : {};
  };

  // 입력 필드 스타일
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };

  // 버튼 컨테이너 스타일
  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '20px'
  };

  // 수정 버튼 스타일
  const editButtonStyle = {
    padding: '8px 16px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s'
  };

  // 취소 버튼 스타일
  const cancelButtonStyle = {
    padding: '8px 16px',
    backgroundColor: secondaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s'
  };

  // 저장 버튼 스타일
  const saveButtonStyle = {
    padding: '8px 16px',
    backgroundColor: successColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
    opacity: isSaving ? 0.7 : 1
  };

  // 오류 메시지 스타일
  const errorMessageStyle = {
    color: '#dc3545',
    marginTop: '10px',
    fontSize: '14px',
    textAlign: 'center'
  };

  // 로딩 오버레이 스타일
  const loadingOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    borderRadius: '8px'
  };

  // 추천링크 스타일
  const referralBoxStyle = {
    marginTop: '30px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    border: '1px solid #ddd'
  };

  // 추천링크 헤더 스타일
  const referralHeaderStyle = {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#333'
  };

  // 추천링크 인풋 스타일
  const referralInputStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  // 추천링크 텍스트 필드 스타일
  const linkFieldStyle = {
    flex: '1',
    padding: '10px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#f1f3f5',
    color: '#495057',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  // 복사 버튼 스타일
  const copyButtonStyle = {
    padding: '10px 15px',
    backgroundColor: copied ? successColor : primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s'
  };

  // 로딩 상태 또는 로그인 안 된 경우 처리
  if (!userInfo) {
    return (
      <div style={defaultStyle}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t('회원 정보')}</h2>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>{t('로그인이 필요합니다.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={defaultStyle}>
      {(isSaving || isReloading) && (
        <div style={loadingOverlayStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px', fontSize: '20px' }}>
              {isSaving ? t('저장 중...') : t('정보 로드 중...')}
            </div>
            <div>{t('잠시만 기다려주세요.')}</div>
          </div>
        </div>
      )}
      
      <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t('회원 정보')}</h2>
      
      {/* 수정/저장 버튼 */}
      <div style={buttonContainerStyle}>
        {isEditing ? (
          <>
            <button 
              onClick={handleCancel} 
              style={cancelButtonStyle}
              disabled={isSaving || isReloading}
            >
              {t('취소')}
            </button>
            <button 
              onClick={handleSave} 
              style={saveButtonStyle}
              disabled={isSaving || isReloading}
            >
              {isSaving ? t('저장 중...') : t('저장')}
            </button>
          </>
        ) : (
          <button 
            onClick={handleEdit} 
            style={editButtonStyle}
            disabled={isSaving || isReloading}
          >
            {t('수정')}
          </button>
        )}
      </div>
      
      {/* 에러 메시지 */}
      {saveError && (
        <div style={errorMessageStyle}>{saveError}</div>
      )}
      
      {/* 유저 정보 섹션 */}
      <div style={{ marginBottom: '30px' }}>
        {/* 아이디 */}
        <div style={profileRowStyle}>
          <div style={labelStyle}>{t('아이디')}</div>
          <div style={valueStyle}>{userInfo.insungId}</div>
        </div>
        
        {/* 이름 */}
        <div style={profileRowStyle}>
          <div style={labelStyle}>{t('이름')}</div>
          <div style={valueStyle}>
            {isEditing ? (
              <input
                type="text"
                style={inputStyle}
                value={editedUserInfo.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            ) : (
              <div style={getHighlightedStyle('name')}>{userInfo.name}</div>
            )}
          </div>
        </div>
        
        {/* 비밀번호 */}
        <div style={profileRowStyle}>
          <div style={labelStyle}>{t('비밀번호')}</div>
          <div style={valueStyle}>
            {isEditing ? (
              <input
                type="password"
                style={inputStyle}
                placeholder={t('새 비밀번호 입력 (변경하지 않으려면 빈칸으로 두세요)')}
                onChange={(e) => handleInputChange('password', e.target.value)}
              />
            ) : (
              <div style={getHighlightedStyle('password')}>••••••••</div>
            )}
          </div>
        </div>
        
        {/* 전화번호 */}
        <div style={profileRowStyle}>
          <div style={labelStyle}>{t('전화번호')}</div>
          <div style={valueStyle}>
            {isEditing ? (
              <input
                type="tel"
                style={inputStyle}
                value={editedUserInfo.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            ) : (
              <div style={getHighlightedStyle('phone')}>{userInfo.phone}</div>
            )}
          </div>
        </div>
        

        {/* 이메일 */}
        {withcookieData?.skin?.company?.id !==24 && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('이메일')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="email"
                  style={inputStyle}
                  value={editedUserInfo.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('email')}>{userInfo.email || '-'}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 생년월일 */}
        {withcookieData?.skin?.company?.id !==24 && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('생년월일')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="date"
                  style={inputStyle}
                  value={editedUserInfo.birthday || ''}
                  onChange={(e) => handleInputChange('birthday', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('birthday')}>
                  {formatBirthdate(userInfo.birthdate || userInfo.birthday)}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 가입일 */}
        {withcookieData?.skin?.company?.id !==24 && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('가입일')}</div>
            <div style={valueStyle}>
              {userInfo.created_at ? new Date(userInfo.created_at).toLocaleDateString('ko-KR') : '-'}
            </div>
          </div>
        )}
        
        {/* 주소 */}
        {withcookieData?.skin?.company?.id !==24 && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('주소')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedUserInfo.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('address')}>{userInfo.address || '-'}</div>
              )}
            </div>
          </div>
        )}

        
        {/* 추천인 - refferer가 있을 때만 표시 */}
        {userInfo.refferer && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('추천인')}</div>
            <div style={valueStyle}>
              <div>{userInfo.refferer.name} ({userInfo.refferer.insungId})</div>
            </div>
          </div>
        )}
        

        {/* var01 커스텀 필드 (보여줄 필드가 있는 경우만 행 렌더링) */}
        {varFields && (varFields.var01?.show) && (

          <div style={profileRowStyle}>
            <div style={labelStyle}>{t(varFields.var01?.label)}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedUserInfo.var01 || ''}
                  onChange={(e) => handleInputChange('var01', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('var01')}>{userInfo.var01 || '-'}</div>
              )}
            </div>
          </div>

        )}
        {varFields && (varFields.var02?.show) && (

          <div style={profileRowStyle}>
            <div style={labelStyle}>{t(varFields.var02?.label)}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedUserInfo.var02 || ''}
                  onChange={(e) => handleInputChange('var02', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('var02')}>{userInfo.var02 || '-'}</div>
              )}
            </div>
          </div>

        )}
        {varFields && (varFields.var03?.show) && (

          <div style={profileRowStyle}>
            <div style={labelStyle}>{t(varFields.var03?.label)}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedUserInfo.var03 || ''}
                  onChange={(e) => handleInputChange('var03', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('var03')}>{userInfo.var03 || '-'}</div>
              )}
            </div>
          </div>

        )}


      </div>
      
      {/* 추천링크 박스 */}
      <div style={referralBoxStyle}>
        <div style={referralHeaderStyle}>{t('추천링크')}</div>
        <div style={referralInputStyle}>
          <div style={linkFieldStyle}>{referralLink}</div>
          <button 
            onClick={copyToClipboard} 
            style={copyButtonStyle}
            title={t('클립보드에 링크 복사')}
          >
            {copied ? t('복사됨!') : t('복사')}
          </button>
        </div>
        {copied && (
          <div style={{ color: 'green', fontSize: '12px', marginTop: '5px' }}>
            {t('클립보드에 복사되었습니다!')}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileComponent; 